// PR-NEOM-2 — simple maintenance entry for daily NEOM use.
//
// The pro sheet (MaintenanceProSheet) stays for detailed entry with a
// parts/labor split and advance reimbursement. This is the fast path the
// operator wants: pick a truck, tap a quick type, one optional cost, one
// optional note, and an OPTIONAL before/after photo. Never mandatory.

import { useEffect, useRef, useState } from 'react';
import { Loader2, Wrench, Camera, CheckCircle2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/Sheet';
import { VehiclePicker, type VehicleOption } from '@/components/common/VehiclePicker';
import { apiClient, extractErrorMessage, extractFieldErrors } from '@/lib/api';
import { ConsumablesPicker, commitConsumables, EMPTY_CONSUMABLES,
  type ConsumableSelection } from '@/components/maintenance/ConsumablesPicker';

// The six quick types map straight onto backend enum values
// (migration 095 adds tire_wear + basic_repair). "Other" forces a note.
const QUICK_TYPES: Array<{ value: string; label: string }> = [
  { value: 'oil_change',      label: 'Oil change' },
  { value: 'tire_puncture',   label: 'Tire puncture' },
  { value: 'tire_replacement', label: 'Tire change' },
  { value: 'tire_wear',       label: 'Tire wear' },
  { value: 'basic_repair',    label: 'Basic repair' },
  { value: 'other',           label: 'Other' },
];

export function MaintenanceQuickLogSheet({
  onClose,
  onSaved,
  onAdvanced,
}: {
  onClose: () => void;
  onSaved: (id: string) => void;
  onAdvanced: () => void;
}) {
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState<VehicleOption | null>(null);
  const [eventType, setEventType] = useState('oil_change');
  const [odometer, setOdometer] = useState('');
  const [cost, setCost] = useState('');
  const [description, setDescription] = useState('');
  const [consumables, setConsumables] = useState<ConsumableSelection>(EMPTY_CONSUMABLES);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  // Two-stage: after save, offer optional before/after photos in the same sheet.
  const [stage, setStage] = useState<'form' | 'media'>('form');
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [uploadedBefore, setUploadedBefore] = useState(0);
  const [uploadedAfter, setUploadedAfter] = useState(0);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const beforeRef = useRef<HTMLInputElement | null>(null);
  const afterRef = useRef<HTMLInputElement | null>(null);

  /*
   * PR-ODO — the reading is carried in from the truck the moment one is picked,
   * and it is no longer optional.
   *
   * Every one of the first hundred and two jobs was saved without it, which is
   * why cost-per-kilometre and every distance-based service interval were
   * unusable. Pre-filling is what makes requiring it fair: the fleet already
   * knows the number, so the field arrives answered and only has to be
   * corrected when the truck has moved since.
   */
  const [odoPrefilled, setOdoPrefilled] = useState(false);
  useEffect(() => {
    if (!vehicleId) { setOdometer(''); setOdoPrefilled(false); return; }
    let cancelled = false;
    apiClient.get<{ currentOdometerKm?: number }>(`/api/v1/vehicles/${vehicleId}`)
      .then((r) => {
        if (cancelled) return;
        if (r.data.currentOdometerKm != null) {
          setOdometer(String(r.data.currentOdometerKm));
          setOdoPrefilled(true);
        } else {
          setOdoPrefilled(false);
        }
      })
      .catch(() => { /* the field simply stays empty and required */ });
    return () => { cancelled = true; };
  }, [vehicleId]);

  const fe = (k: string) => fieldErrors[k]?.[0];
  const needsNote = eventType === 'other';

  // How this job is allowed to close, decided by what it is and what it cost.
  const TYRE_TYPES = ['tire_replacement', 'tire_rotation', 'tire_puncture', 'tire_wear'];
  const isTyre = TYRE_TYPES.includes(eventType);
  const costNum = Number(cost) || 0;
  const isMinor = !isTyre && costNum > 0 && costNum <= 500;

  // Photos taken on the fast-track path: the fitted tyre, or the receipt.
  const fastRef = useRef<HTMLInputElement | null>(null);
  const [fastPhotos, setFastPhotos] = useState(0);

  async function uploadPhoto(file: File, kind: 'before' | 'after' | 'new_part' | 'invoice') {
    if (!createdId) return;
    setUploadBusy(true); setUploadError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      await apiClient.post(`/api/v1/maintenance/events/${createdId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (kind === 'before') setUploadedBefore((n) => n + 1);
      else if (kind === 'after') setUploadedAfter((n) => n + 1);
      else setFastPhotos((n) => n + 1);
    } catch (err) {
      setUploadError(extractErrorMessage(err));
    } finally { setUploadBusy(false); }
  }

  async function submit() {
    setError(null);
    setFieldErrors({});
    if (!vehicleId) { setFieldErrors({ vehicleId: ['Pick a vehicle'] }); return; }
    if (!odometer.trim() || Number(odometer) <= 0) {
      setFieldErrors({ odometer: ['Record the odometer reading — every service interval is counted from it'] });
      return;
    }
    if (needsNote && !description.trim()) { setFieldErrors({ description: ['Add a short description'] }); return; }
    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        vehicleId,
        eventType,
        priority: 'normal',
        // Capture the driver at log time so attribution survives reassignment.
        ...(vehicle?.currentDriver?.id && { reportedByDriverId: vehicle.currentDriver.id }),
        ...(description.trim() && { description: description.trim() }),
        ...(odometer && { odometerAtService: Number(odometer) }),
        ...(Number(cost) > 0 && { otherCostSar: Number(cost) }),
      };
      const r = await apiClient.post<{ id: string; code: string }>('/api/v1/maintenance/events', payload);
      // Oil and tyres, charged to the job just created. The job is already
      // saved, so a short drum is reported rather than losing the log.
      const trouble = await commitConsumables(r.data.id, vehicleId, consumables);
      if (trouble.length) setError(trouble.join(' '));
      setCreatedId(r.data.id);
      setCreatedCode(r.data.code);
      setStage('media');
    } catch (err) {
      const fes = extractFieldErrors(err);
      if (Object.keys(fes).length > 0) { setFieldErrors(fes); setError('Some fields need attention.'); }
      else setError(extractErrorMessage(err));
    } finally { setBusy(false); }
  }

  /**
   * The two shortcuts, taken from the same button. A tyre closes through
   * /settle, which is the only door still open to it; a minor repair closes
   * through /close-minor, which checks the ceiling, the reading and the
   * receipt on the server. Neither can be widened from here.
   */
  const [closing, setClosing] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [closedOk, setClosedOk] = useState(false);

  async function closeFast() {
    if (!createdId) return;
    setClosing(true); setCloseError(null);
    try {
      await apiClient.post(
        `/api/v1/maintenance/events/${createdId}/${isTyre ? 'settle' : 'close-minor'}`,
        isTyre ? { paid: true } : {},
      );
      setClosedOk(true);
      onSaved(createdId);
    } catch (err) {
      setCloseError(extractErrorMessage(err));
    } finally { setClosing(false); }
  }

  function finalize() { if (createdId) onSaved(createdId); }

  return (
    <Sheet open onOpenChange={(o) => { if (!o && !busy && !uploadBusy) { stage === 'media' ? finalize() : onClose(); } }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" /> {stage === 'form' ? 'Quick log' : `Photos — ${createdCode ?? ''}`}
          </SheetTitle>
        </SheetHeader>

        {stage === 'media' ? (
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-quicklog-media>
            <p className="text-sm text-muted-foreground">
              Saved <span className="font-mono">{createdCode}</span>.{' '}
              {isTyre
                ? 'Photograph the tyre and it closes here.'
                : isMinor
                  ? 'Photograph the receipt and it closes here.'
                  : 'Add a before photo now if you have one — the job cannot be closed later without a before and an after.'}
            </p>

            {/* The two declared shortcuts. Everything else walks the ladder,
                and is told so rather than left to discover it. */}
            {(isTyre || isMinor) && (
              <div className="rounded-md border border-border bg-card p-3 space-y-2">
                <div className="text-sm font-medium">
                  {isTyre ? 'Close this tyre job' : 'Close as a minor repair'}
                </div>
                <p className="text-xs text-muted-foreground">
                  {isTyre
                    ? 'Recorded, not inspected: the reading and the photograph are the whole record.'
                    : `Under the 500 SAR ceiling. The cost, the reading and the receipt are the whole record.`}
                </p>
                <input ref={fastRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    // The kind is the truth about what the picture is: a tyre
                    // that was fitted, or the bill that was paid.
                    if (f) uploadPhoto(f, isTyre ? 'new_part' : 'invoice');
                    e.target.value = '';
                  }} />
                <Button size="sm" variant="outline" className="w-full gap-1.5"
                  onClick={() => fastRef.current?.click()} disabled={uploadBusy || closing}>
                  <Camera className="h-3.5 w-3.5" />
                  {fastPhotos > 0
                    ? `${fastPhotos} attached — add another`
                    : (isTyre ? 'Photograph the tyre' : 'Photograph the receipt')}
                </Button>
                <Button size="sm" variant="gold" className="w-full"
                  disabled={closing || fastPhotos < 1}
                  onClick={closeFast}>
                  {closing ? 'Closing…' : fastPhotos < 1
                    ? (isTyre ? 'Attach the tyre photo first' : 'Attach the receipt first')
                    : 'Record it and close'}
                </Button>
                {closeError && <p className="text-xs text-rose-600 dark:text-rose-400">{closeError}</p>}
                {closedOk && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Closed and recorded.
                  </p>
                )}
              </div>
            )}

            {/* Before/after belong to a job that will be closed through the
                ladder. A tyre and a minor repair are closed here instead, and
                showing them both would only invite the wrong picture. */}
            {!(isTyre || isMinor) && (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-border p-3 space-y-2 text-center">
                <div className="text-sm font-medium">Before</div>
                <div className="text-xs text-muted-foreground tabular-nums">{uploadedBefore} attached</div>
                <input ref={beforeRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f, 'before'); e.target.value = ''; }} />
                <Button variant="outline" size="sm" onClick={() => beforeRef.current?.click()} disabled={uploadBusy} className="w-full gap-1">
                  <Camera className="h-3.5 w-3.5" /> Before
                </Button>
              </div>
              <div className="rounded-md border border-border p-3 space-y-2 text-center">
                <div className="text-sm font-medium">After</div>
                <div className="text-xs text-muted-foreground tabular-nums">{uploadedAfter} attached</div>
                <input ref={afterRef} type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f, 'after'); e.target.value = ''; }} />
                <Button variant="outline" size="sm" onClick={() => afterRef.current?.click()} disabled={uploadBusy} className="w-full gap-1">
                  <Camera className="h-3.5 w-3.5" /> After
                </Button>
              </div>
            </div>
            )}

            {uploadError && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{uploadError}</div>}
            {!(isTyre || isMinor) && (
              <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 p-3 text-sm flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  This job is open at <b>Reported</b>. Next it needs a diagnosis — open it from the
                  maintenance list to take it forward.
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-4" data-quicklog-form>
            <div className="space-y-1.5">
              <Label>Vehicle</Label>
              <VehiclePicker value={vehicleId} preferProjectScope="neom"
                onChange={(id, v) => { setVehicleId(id); setVehicle(v); }} placeholder="Pick a truck…" />
              {fe('vehicleId') && <p className="text-xs text-rose-600" data-error-vehicleId>{fe('vehicleId')}</p>}
              {vehicle && (
                <p className="text-xs text-muted-foreground">
                  Driver: <span className="font-medium text-foreground">{vehicle.currentDriver?.fullNameEn ?? '— none assigned —'}</span>
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Type</Label>
              <div className="grid grid-cols-2 gap-2" data-quicklog-types>
                {QUICK_TYPES.map((qt) => (
                  <button key={qt.value} type="button" onClick={() => setEventType(qt.value)}
                    className={'rounded-md border px-3 py-2.5 text-sm font-medium transition-colors ' +
                      (eventType === qt.value
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-input text-muted-foreground hover:bg-secondary')}>
                    {qt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="ql-cost">
                  Cost SAR {isMinor && <span className="text-emerald-600 dark:text-emerald-400 font-normal">· minor repair</span>}
                </Label>
                <Input id="ql-cost" type="number" step="0.01" min="0" value={cost}
                  onChange={(e) => setCost(e.target.value)} placeholder="0" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ql-odo">Odometer km <span className="text-rose-600 dark:text-rose-400">*</span></Label>
                <Input id="ql-odo" type="number" min="0" value={odometer}
                  onChange={(e) => { setOdometer(e.target.value); setOdoPrefilled(false); }}
                  className={fe('odometer') ? 'border-rose-500' : undefined} />
                {fe('odometer')
                  ? <p className="text-xs text-rose-600" data-error-odometer>{fe('odometer')}</p>
                  : odoPrefilled
                    ? <p className="text-[11px] text-muted-foreground">From this truck&rsquo;s last reading — correct it if it has moved.</p>
                    : null}
              </div>
            </div>

            {/* Said before the save, so nobody is surprised by where the job
                lands. The ladder is the default; the two shortcuts are named. */}
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
              {isTyre
                ? 'Tyre work closes in one step — this and a photograph of the tyre are all it needs.'
                : isMinor
                  ? `Under 500 SAR, so this can close in one step once you attach the receipt.`
                  : 'This opens at Reported. It needs a diagnosis, then the work, photographs, an invoice, a check and an approval before it can be paid.'}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ql-desc">{needsNote ? 'Description (required)' : 'Note (optional)'}</Label>
              <textarea id="ql-desc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000}
                className={`flex w-full rounded-md border bg-background px-3 py-2 text-base ${fe('description') ? 'border-rose-500' : 'border-input'}`}
                placeholder={needsNote ? 'What happened?' : 'Anything to add?'} />
              {fe('description') && <p className="text-xs text-rose-600" data-error-description>{fe('description')}</p>}
            </div>

            {/* Oil and tyres, taken at the moment the job is logged. */}
            <ConsumablesPicker value={consumables} onChange={setConsumables} compact />

            {error && <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm" data-quicklog-error>{error}</div>}

            <button type="button" onClick={onAdvanced}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
              Need parts/labor split or advance reimbursement? Open detailed entry <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}

        <SheetFooter>
          {stage === 'form' ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={busy || !vehicleId} className="gap-2" data-quicklog-submit>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save
              </Button>
            </>
          ) : (
            <Button onClick={finalize} disabled={uploadBusy} className="gap-2" data-quicklog-done>
              <CheckCircle2 className="h-4 w-4" /> Done
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
